/**
 * v0.4 章④ F-2 (design-expert L-1): shift_violations.detail_json の汎用表示。
 * ShiftBoardPage.tsx / ShiftCoordinationPage.tsx の両方が同じ shift_violations レコードの
 * detail を表示するため、独立に2実装を持たせず1箇所に集約する (§8-55 対策)。
 */
const formatMinutes = (m: number) => {
  const h = Math.floor(((m % 1440) + 1440) % 1440 / 60);
  const min = ((m % 1440) + 1440) % 1440 % 60;
  return `${String(h).padStart(2, '0')}:${String(min).padStart(2, '0')}`;
};

const formatDetailValue = (key: string, v: unknown): string => {
  if (key === 'window' && v && typeof v === 'object' && 'start' in v && 'end' in v) {
    const w = v as { start: number; end: number };
    return `営業時間 ${formatMinutes(w.start)}〜${formatMinutes(w.end)}`;
  }
  return `${key}: ${String(v)}`;
};

/** exemption_kind の日本語ラベル (basis は意図的に含めない、§7-3 可視性ルール議論)。 */
const EXEMPTION_KIND_LABELS: Record<string, string> = {
  night_work: '深夜業', overtime: '時間外労働', holiday_work: '休日労働', non_scheduled: '所定外労働',
};

export function ViolationDetail({ detail, kind }: { detail: Record<string, unknown> | null; kind?: string }) {
  if (!detail) return null;
  // 人・組織機能 増分④ (security-reviewer Medium指摘への対応): 法令ガード由来の2種は detail の
  // キーを汎用フォールバック (Object.entries) に流さず、明示的なallowlistで描画する。
  // legal_guard_undeterminable は常に detail={} (lib/shift-violations.ts の
  // detectLegalGuardUndeterminable 参照) のため今は実害が無いが、将来 detail に生年月日等の
  // 機微情報が誤って追加されても汎用描画で漏れないよう、kind単位の構造的な歯止めを持たせる。
  if (kind === 'legal_guard_undeterminable') {
    return <span>生年月日が未登録のため年少者・免除ガードを判定できません</span>;
  }
  if (kind === 'exemption_violation') {
    const exemptionKind = typeof detail.exemption_kind === 'string' ? detail.exemption_kind : null;
    return <span>免除請求 ({exemptionKind ? EXEMPTION_KIND_LABELS[exemptionKind] ?? exemptionKind : '種別不明'}) に抵触します</span>;
  }
  if (kind === 'minor_night_work') return <span>年少者を深夜帯に配置しています</span>;
  if (kind === 'minor_holiday_work') return <span>年少者を法定休日に配置しています</span>;
  if (kind === 'minor_overtime') {
    const parts: string[] = [];
    if (detail.daily_exceeded) parts.push('1日8時間超');
    if (detail.weekly_exceeded) parts.push('週40時間超');
    return <span>年少者の労働時間が法定基準を超えています ({parts.join('・') || '基準超過'})</span>;
  }
  if (typeof detail.required_value === 'number' || typeof detail.actual_value === 'number') {
    return <span>必要 {String(detail.required_value ?? detail.effective_value ?? '−')} ・ 実績 {String(detail.actual_value ?? '−')}</span>;
  }
  // v0.4 追加7要件⑨ (D-255-6): statutory_holiday_missing 専用の表示。有休・欠勤も休みとして
  // 数えている (判定が甘くなる側の割り切り) ことを常に明示する必須要件。
  if (typeof detail.rest_days === 'number' && typeof detail.grant_days === 'number' && 'includes_leave_and_absence' in detail) {
    const unitLabel = detail.grant_unit === 'four_week' ? '4週間' : '週';
    return (
      <span>
        {unitLabel}あたり必要{String(detail.grant_days)}日の休みに対し、実際の休みは{String(detail.rest_days)}日でした
        (有休・欠勤を含みます)
      </span>
    );
  }
  // v0.4 TD-25 (D-261/D-262): contracted_days_shortfall 専用の表示。個人上書きかノード既定かの
  // 出所 (D-59 必須要件) を常に明示する。
  if (typeof detail.worked_days === 'number' && typeof detail.contracted_days === 'number') {
    // 契約区分マスタの残業計算連携 (migration 0192、§13-42): design-and-codeレビュー指摘HIGH是正
    // (D-59必須要件、出所の明示から新設の 'contract_class' が漏れていた)。
    const sourceLabel = detail.source_kind === 'person' ? '個別の契約'
      : detail.source_kind === 'contract_class' ? '契約区分マスタ'
        : detail.source_kind === 'self' || detail.source_kind === 'ancestor' || detail.source_kind === 'tenant_default' ? '組織の標準値' : null;
    return (
      <span>
        週あたり所定{String(detail.contracted_days)}日に対し、実際の勤務は{String(detail.worked_days)}日でした
        {sourceLabel ? `(${sourceLabel}による判定)` : ''}
      </span>
    );
  }
  const entries = Object.entries(detail).filter(([k]) => k !== 'assignment_id');
  if (entries.length === 0) return null;
  return <span>{entries.map(([k, v]) => formatDetailValue(k, v)).join(' / ')}</span>;
}
